import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Authentication Higher-Order Component
 *
 * Robust auth wrapper that prevents logout loops and handles errors gracefully.
 * Use this for ALL pages that require authentication.
 *
 * Benefits:
 * - Automatic auth checking
 * - Graceful error handling (no unexpected logouts)
 * - Loading states
 * - Redirect to login if needed
 * - Prevents auth bugs in new pages
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { PageContainer, PageTitle, Text } from './PageStyles';
import { RoleLevel } from '../types/rbacImproved';
/**
 * HOC to wrap any page component with authentication
 *
 * Usage:
 * ```tsx
 * const ProtectedProfile = withAuth(Profile, {
 *   minimumRole: RoleLevel.BASIC_DEALER
 * });
 * ```
 */
export function withAuth(Component, options = {}) {
    const { minimumRole, redirectTo = '/login', showLoading = true, loadingComponent, showErrorInline = false } = options;
    return function AuthenticatedComponent(props) {
        const navigate = useNavigate();
        const { user, isAuthenticated, isLoading: authLoading } = useAuthStore();
        const [isChecking, setIsChecking] = useState(true);
        const [authError, setAuthError] = useState(null);
        useEffect(() => {
            const checkAccess = () => {
                setIsChecking(true);
                setAuthError(null);
                // Wait for auth to finish loading
                if (authLoading) {
                    return;
                }
                // Check if user is authenticated
                if (!isAuthenticated || !user) {
                    if (showErrorInline) {
                        setAuthError('Please log in to access this page');
                    }
                    else {
                        console.log('🔐 withAuth - Not authenticated, redirecting to login');
                        navigate(redirectTo, { replace: true });
                    }
                    setIsChecking(false);
                    return;
                }
                // Check role level if required
                if (minimumRole !== undefined && user.roleLevel < minimumRole) {
                    const errorMsg = `This page requires ${RoleLevel[minimumRole]} access or higher`;
                    if (showErrorInline) {
                        setAuthError(errorMsg);
                    }
                    else {
                        console.log(`🔐 withAuth - Insufficient role level, redirecting`);
                        navigate('/', { replace: true });
                    }
                    setIsChecking(false);
                    return;
                }
                // All checks passed
                setIsChecking(false);
            };
            checkAccess();
        }, [isAuthenticated, user, authLoading, navigate, minimumRole, redirectTo, showErrorInline]);
        // Show loading state
        if (isChecking || authLoading) {
            if (!showLoading) {
                return null;
            }
            if (loadingComponent) {
                return _jsx(_Fragment, { children: loadingComponent });
            }
            return (_jsxs(PageContainer, { children: [_jsx(PageTitle, { children: "Loading..." }), _jsx(Text, { children: "Checking authentication..." })] }));
        }
        // Show inline error if configured
        if (authError && showErrorInline) {
            return (_jsxs(PageContainer, { children: [_jsx(PageTitle, { children: "Access Denied" }), _jsx(Text, { style: { color: '#ef4444' }, children: authError })] }));
        }
        // Render protected component
        if (!authError) {
            return _jsx(Component, { ...props });
        }
        // Fallback (should never reach here due to redirects above)
        return null;
    };
}
/**
 * Convenience wrappers for common auth levels
 */
export const withDealerAuth = (Component) => withAuth(Component, { minimumRole: RoleLevel.BASIC_DEALER });
export const withVerifiedDealerAuth = (Component) => withAuth(Component, { minimumRole: RoleLevel.VERIFIED_DEALER });
export const withAdminAuth = (Component) => withAuth(Component, { minimumRole: RoleLevel.SALES_MANAGER });
export const withSuperAdminAuth = (Component) => withAuth(Component, { minimumRole: RoleLevel.SUPER_ADMIN });
export default withAuth;
